import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, Mail, ArrowLeft } from 'lucide-react';

const Terms = () => {
  return (
    <div className="pt-16">
      <section className="relative py-20 bg-gradient-to-br from-primary-600 via-accent-600 to-primary-700 text-white">
        <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <FileText className="w-16 h-16 mx-auto mb-6" />
          <h1 className="text-4xl md:text-5xl font-display font-bold mb-4">Terms & Conditions</h1>
          <p className="text-xl text-primary-100">Last updated: December 2025</p>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/home" className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-8 font-medium">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>

          <div className="prose prose-lg max-w-none">
            <h2 className="text-2xl font-display font-bold text-gray-900 mt-8 mb-4">1. Agreement to Terms</h2>
            <p className="text-gray-600 mb-4">
              These Terms and Conditions ("Terms") constitute a legally binding agreement between you ("Client," "you," or "your") and MMI Online ("Company," "we," "our," or "us") governing your access to and use of our website and AI-powered business automation services.
            </p>
            <p className="text-gray-600 mb-4">
              By accessing our website or using our services, you agree to be bound by these Terms. If you disagree with any part of these Terms, you may not access our website or use our services.
            </p>

            <h2 className="text-2xl font-display font-bold text-gray-900 mt-8 mb-4">2. Description of Services</h2>
            <p className="text-gray-600 mb-4">MMI Online provides AI-powered business automation services, including but not limited to:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
              <li><strong>Database Reactivation:</strong> Automated SMS and messaging campaigns to re-engage dormant leads and customers in your database</li>
              <li><strong>Speed To Lead:</strong> Instant AI-powered response system to engage new leads 24/7</li>
              <li><strong>Out Of Hours:</strong> Automated lead qualification and engagement outside business hours</li>
              <li><strong>Google Reviews:</strong> AI-powered automated responses to Google reviews</li>
              <li><strong>Voice AI:</strong> Automated inbound call handling, appointment booking, and information gathering</li>
              <li><strong>AI Advertisements:</strong> Professional business advertisements made to look human-like using AI</li>
            </ul>

            <h2 className="text-2xl font-display font-bold text-gray-900 mt-8 mb-4">3. Account Registration and Eligibility</h2>
            <p className="text-gray-600 mb-4">To use our services, you must:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
              <li>Be at least 18 years of age</li>
              <li>Have the legal authority to enter into binding contracts</li>
              <li>Provide accurate and complete registration information</li>
              <li>Maintain the security of your account credentials</li>
              <li>Notify us immediately of any unauthorized access to your account</li>
            </ul>
            <p className="text-gray-600 mb-4">
              You are responsible for all activities that occur under your account.
            </p>

            <h2 className="text-2xl font-display font-bold text-gray-900 mt-8 mb-4">4. Service Plans and Pricing</h2>
            <h3 className="text-xl font-display font-semibold text-gray-900 mt-6 mb-3">4.1 Subscription Plans</h3>
            <p className="text-gray-600 mb-4">We offer the following subscription plans:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
              <li><strong>Bronze (£200/month, £1,000 setup fee):</strong> Voice AI with 4-minute call limit</li>
              <li><strong>Silver (£300/month, £1,200 setup fee):</strong> Voice AI with unlimited call minutes, Google Reviews, and Chatbot</li>
              <li><strong>Gold (£400/month, £1,300 setup fee):</strong> All features including Voice AI, Google Reviews, Chatbot, Booking Appointments via Calendar, and AI Advertisements</li>
            </ul>
            <p className="text-gray-600 mb-4"><strong>Note:</strong> Database Reactivation is available as a separate service.</p>

            <h3 className="text-xl font-display font-semibold text-gray-900 mt-6 mb-3">4.2 Payment Terms</h3>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
              <li>All fees are quoted in GBP (British Pounds) unless otherwise stated</li>
              <li>Subscription fees are billed monthly in advance</li>
              <li>Payment is due upon invoice</li>
              <li>We accept major credit cards and bank transfers</li>
              <li>All fees are non-refundable unless otherwise stated</li>
            </ul>

            <h3 className="text-xl font-display font-semibold text-gray-900 mt-6 mb-3">4.3 Price Changes</h3>
            <p className="text-gray-600 mb-4">
              We reserve the right to modify our pricing with 30 days' notice. Price changes will take effect at the start of your next billing cycle.
            </p>

            <h2 className="text-2xl font-display font-bold text-gray-900 mt-8 mb-4">5. Client Responsibilities</h2>
            <p className="text-gray-600 mb-4">As a client, you agree to:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
              <li>Provide accurate and lawfully obtained customer data for our services to process</li>
              <li>Ensure you have proper consent to contact individuals in your database</li>
              <li>Comply with all applicable laws, including data protection and anti-spam regulations (GDPR, TCPA, CAN-SPAM, PECR, etc.)</li>
              <li>Not use our services for any unlawful, fraudulent, or harmful purposes</li>
              <li>Not send spam, unsolicited messages, or harassing communications</li>
              <li>Maintain accurate and up-to-date opt-out lists</li>
              <li>Respond promptly to any data subject requests</li>
            </ul>

            <h2 className="text-2xl font-display font-bold text-gray-900 mt-8 mb-4">6. Data Protection and Processing</h2>
            <h3 className="text-xl font-display font-semibold text-gray-900 mt-6 mb-3">6.1 Data Controller and Processor</h3>
            <p className="text-gray-600 mb-4">
              You remain the data controller for any personal data you provide to us. We act as a data processor, processing data only on your behalf and in accordance with your instructions.
            </p>

            <h3 className="text-xl font-display font-semibold text-gray-900 mt-6 mb-3">6.2 Data Processing Agreement</h3>
            <p className="text-gray-600 mb-4">
              By using our services, you agree to our Data Processing Agreement, which forms part of these Terms. We will process personal data in accordance with applicable data protection laws.
            </p>

            <h3 className="text-xl font-display font-semibold text-gray-900 mt-6 mb-3">6.3 Your Obligations</h3>
            <p className="text-gray-600 mb-4">You warrant that:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
              <li>You have a lawful basis to process the personal data you provide</li>
              <li>You have obtained all necessary consents for SMS and email marketing</li>
              <li>You will honor opt-out requests promptly</li>
              <li>You will not provide data that violates any third-party rights</li>
            </ul>

            <h2 className="text-2xl font-display font-bold text-gray-900 mt-8 mb-4">7. Messaging and Communication Compliance</h2>
            <p className="text-gray-600 mb-4">When using our SMS and messaging services, you agree to:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
              <li>Comply with all applicable telecommunications and messaging regulations</li>
              <li>Only message individuals who have provided proper consent</li>
              <li>Include appropriate opt-out mechanisms in all messages</li>
              <li>Honor opt-out requests within the legally required timeframe</li>
              <li>Not send messages during prohibited hours (where applicable)</li>
              <li>Maintain records of consent for a minimum of 4 years</li>
            </ul>
            <p className="text-gray-600 mb-4">
              <strong>Important:</strong> You are solely responsible for ensuring compliance with messaging regulations. We provide the technology platform, but compliance responsibility rests with you.
            </p>

            <h2 className="text-2xl font-display font-bold text-gray-900 mt-8 mb-4">8. Intellectual Property</h2>
            <h3 className="text-xl font-display font-semibold text-gray-900 mt-6 mb-3">8.1 Our Intellectual Property</h3>
            <p className="text-gray-600 mb-4">
              All content, features, and functionality of our website and services, including but not limited to text, graphics, logos, software, and AI models, are owned by MMI Online and protected by intellectual property laws.
            </p>

            <h3 className="text-xl font-display font-semibold text-gray-900 mt-6 mb-3">8.2 License to Use</h3>
            <p className="text-gray-600 mb-4">
              We grant you a limited, non-exclusive, non-transferable license to access and use our services for your internal business purposes during your subscription period.
            </p>

            <h3 className="text-xl font-display font-semibold text-gray-900 mt-6 mb-3">8.3 Your Content</h3>
            <p className="text-gray-600 mb-4">
              You retain ownership of any data, content, or materials you provide to us. You grant us a license to use this content solely for the purpose of providing our services.
            </p>

            <h2 className="text-2xl font-display font-bold text-gray-900 mt-8 mb-4">9. Service Level and Availability</h2>
            <p className="text-gray-600 mb-4">
              We strive to maintain high service availability but do not guarantee uninterrupted access. We may perform scheduled maintenance with reasonable notice. We are not liable for any downtime or service interruptions beyond our reasonable control.
            </p>

            <h2 className="text-2xl font-display font-bold text-gray-900 mt-8 mb-4">10. Limitation of Liability</h2>
            <p className="text-gray-600 mb-4">To the maximum extent permitted by law:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
              <li>Our services are provided "as is" without warranties of any kind</li>
              <li>We do not guarantee specific results or outcomes from using our services</li>
              <li>We are not liable for any indirect, incidental, special, consequential, or punitive damages</li>
              <li>Our total liability shall not exceed the fees paid by you in the 12 months preceding the claim</li>
              <li>We are not responsible for the content of messages sent through our platform</li>
              <li>We are not liable for any regulatory fines or penalties resulting from your non-compliance</li>
            </ul>

            <h2 className="text-2xl font-display font-bold text-gray-900 mt-8 mb-4">11. Indemnification</h2>
            <p className="text-gray-600 mb-4">
              You agree to indemnify, defend, and hold harmless MMI Online and its officers, directors, employees, and agents from any claims, damages, losses, liabilities, and expenses (including legal fees) arising from:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
              <li>Your use of our services</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any applicable laws or regulations</li>
              <li>Any claims by third parties related to messages sent through our platform</li>
              <li>Any data protection or privacy violations</li>
            </ul>

            <h2 className="text-2xl font-display font-bold text-gray-900 mt-8 mb-4">12. Termination</h2>
            <h3 className="text-xl font-display font-semibold text-gray-900 mt-6 mb-3">12.1 Termination by You</h3>
            <p className="text-gray-600 mb-4">
              You may cancel your subscription at any time by contacting us. Cancellation will take effect at the end of your current billing period.
            </p>

            <h3 className="text-xl font-display font-semibold text-gray-900 mt-6 mb-3">12.2 Termination by Us</h3>
            <p className="text-gray-600 mb-4">We may suspend or terminate your access immediately if:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
              <li>You breach these Terms</li>
              <li>You fail to pay fees when due</li>
              <li>Your use of our services poses a risk to us or other users</li>
              <li>We are required to do so by law</li>
            </ul>

            <h3 className="text-xl font-display font-semibold text-gray-900 mt-6 mb-3">12.3 Effect of Termination</h3>
            <p className="text-gray-600 mb-4">
              Upon termination, your right to use our services will cease immediately. We will delete your data in accordance with our Privacy Policy, unless retention is required by law.
            </p>

            <h2 className="text-2xl font-display font-bold text-gray-900 mt-8 mb-4">13. Dispute Resolution</h2>
            <p className="text-gray-600 mb-4">
              Any disputes arising from these Terms shall be resolved through good-faith negotiation. If negotiation fails, disputes shall be submitted to binding arbitration in accordance with the rules of the relevant arbitration body in our jurisdiction.
            </p>

            <h2 className="text-2xl font-display font-bold text-gray-900 mt-8 mb-4">14. Governing Law</h2>
            <p className="text-gray-600 mb-4">
              These Terms shall be governed by and construed in accordance with the laws of England and Wales, without regard to conflict of law principles.
            </p>

            <h2 className="text-2xl font-display font-bold text-gray-900 mt-8 mb-4">15. Changes to Terms</h2>
            <p className="text-gray-600 mb-4">
              We reserve the right to modify these Terms at any time. We will provide notice of material changes by posting the updated Terms on our website and updating the "Last updated" date. Your continued use of our services after such changes constitutes acceptance of the new Terms.
            </p>

            <h2 className="text-2xl font-display font-bold text-gray-900 mt-8 mb-4">16. Severability</h2>
            <p className="text-gray-600 mb-4">
              If any provision of these Terms is found to be unenforceable, the remaining provisions will continue in full force and effect.
            </p>

            <h2 className="text-2xl font-display font-bold text-gray-900 mt-8 mb-4">17. Entire Agreement</h2>
            <p className="text-gray-600 mb-4">
              These Terms, together with our Privacy Policy and any service-specific agreements, constitute the entire agreement between you and MMI Online regarding your use of our services.
            </p>

            <h2 className="text-2xl font-display font-bold text-gray-900 mt-8 mb-4">18. Contact Information</h2>
            <p className="text-gray-600 mb-4">
              For questions about these Terms, please contact us at:
            </p>
            <div className="bg-gray-50 rounded-xl p-6 mb-8">
              <p className="text-gray-700 font-semibold">MMI Online</p>
              <p className="text-gray-600">Email: contact@mmionline.online</p>
              <p className="text-gray-600">Phone our Voice AI bot: 07427 860249</p>
            </div>

            <div className="flex items-center justify-center space-x-4 mt-12 pt-8 border-t border-gray-200">
              <Link to="/privacy" className="text-primary-600 hover:text-primary-700 font-medium">
                View Privacy Policy
              </Link>
              <span className="text-gray-300">|</span>
              <Link to="/contact" className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium">
                <Mail className="w-4 h-4 mr-2" />
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Terms;
